// A game's puzzle list: read from the plugin's folder, or made from a
// complete scan and stored there. The plugin's store of the list's state is
// passed in (the zustand store in `index.tsx`, a plain object in tests), so
// this runs without React.
import { uciToInput, type Chess } from '@chess/api-chess';
import type { Analysis } from '@chess/api-analysis';
import type { Games } from '@chess/api-games';
import type { OpeningBook, Openings } from '@chess/api-openings';
import type { Log } from '@chess/api-log';
import type { Storage } from '@chess/api-storage';
import type { Scan } from '@chess/api-model';
import { FINDER_VERSION, findPuzzles, findTheory, markInOpening, readerSide, type FinderRules, type Puzzle } from './finder.ts';
import { readAttempts, readPuzzles, writePuzzles, type Attempts, type PuzzleList } from './files.ts';
import type { Solving } from './solving.ts';

/** The plugin's folder inside a game. */
export const FOLDER = 'puzzles';

export type LoadStatus = 'idle' | 'loading' | 'scanning' | 'ready' | 'failed';

/** The plugin's state: the open game's list, its attempts and the puzzle being solved. */
export interface PuzzleState {
  gameId?: string;
  status: LoadStatus;
  scanned: number;
  total: number;
  puzzles: Puzzle[];
  attempts: Attempts['attempts'];
  solving?: Solving;
  error?: string;
}

/** Where the state lives: the zustand store's own `getState`/`setState` fit. */
export interface PuzzleStateStore {
  getState(): PuzzleState;
  setState(partial: Partial<PuzzleState>): void;
}

/** The rules of chess as the finder uses them, over the chess API. */
export function finderRules(chess: Pick<Chess, 'play'>): FinderRules {
  return {
    play: (fen, move) => {
      const played = chess.play(fen, move);
      return played ? { fen: played.fen, captured: played.move.captured, checkmate: played.move.flags.checkmate } : undefined;
    },
  };
}

export interface ListLoaderDeps {
  games: Pick<Games, 'get' | 'folder'>;
  chess: Pick<Chess, 'play' | 'parsePgn'>;
  analysis: Pick<Analysis, 'get' | 'scan' | 'status'>;
  /** The openings API when it is there; it is optional, and the caller bounds the wait. */
  openings?: () => Promise<Pick<Openings, 'load'> | undefined>;
  log?: Pick<Log, 'info' | 'warn'>;
  store: PuzzleStateStore;
  /** A list is ready for the game: its counts can go to the phase tabs. */
  onReady?(puzzles: Puzzle[]): void;
}

export function createListLoader(deps: ListLoaderDeps) {
  const { games, chess, analysis, log, store } = deps;
  const folderOf = (gameId: string): Storage => games.folder(gameId, FOLDER);

  /** The stored list and attempts, without generating anything. */
  const readStored = async (gameId: string): Promise<{ list?: PuzzleList; attempts: Attempts }> => {
    const folder = folderOf(gameId);
    const [list, attempts] = await Promise.all([readPuzzles(folder), readAttempts(folder)]);
    return { list, attempts };
  };

  /**
   * Flags the puzzles whose position the opening book still knows, so the
   * move before them was part of an opening. Without the openings API, or
   * its book, nothing is flagged.
   */
  const markOpening = async (puzzles: Puzzle[], firstMove: string | undefined): Promise<OpeningBook | undefined> => {
    if (!firstMove) return undefined;
    const openings = await (deps.openings?.() ?? Promise.resolve(undefined)).catch(() => undefined);
    if (!openings) return undefined;
    try {
      const book = await openings.load(firstMove);
      markInOpening(puzzles, book);
      return book;
    } catch (error) {
      log?.warn('opening book unavailable for puzzle labels', { error: String(error) });
      return undefined;
    }
  };

  /** Makes the list from a complete scan and stores it. */
  const generate = async (gameId: string, scan: Scan): Promise<PuzzleList> => {
    const record = await games.get(gameId);
    if (!record) throw new Error('game not found');
    const parsed = chess.parsePgn(record.pgn);
    const side = readerSide(record);
    const finderGame = { moves: parsed.moves, positions: parsed.positions };
    const puzzles = findPuzzles(scan, finderGame, side, finderRules(chess));
    const book = await markOpening(puzzles, parsed.moves[0]?.san);
    // The opening's own puzzle: the move that completes the opening with the
    // theory, the book's moves as the answer or the engine's best once the
    // theory has run out.
    if (book) {
      const reading = book.book(parsed.positions);
      const theory = findTheory(finderGame, reading, side, {
        continuations: (fen) => book.continuations(fen),
        bestSan: (ply) => {
          const uci = scan.positions.find((p) => p.ply === ply)?.lines[0]?.uci;
          const fen = parsed.positions[ply];
          return uci && fen ? chess.play(fen, uciToInput(uci))?.move.san : undefined;
        },
        swingAt: (ply) => scan.moves.find((m) => m.ply === ply + 1)?.delta ?? 0,
      });
      if (theory) puzzles.push(theory);
    }
    const list: PuzzleList = { schemaVersion: 1, finderVersion: FINDER_VERSION, gameId, scanUpdatedAt: scan.updatedAt, puzzles };
    await writePuzzles(folderOf(gameId), list);
    log?.info('puzzles found', { gameId, count: puzzles.length, finderVersion: FINDER_VERSION });
    return list;
  };

  /** What a phase section does when it first shows for a game: use the stored list, or scan and generate. */
  const load = async (gameId: string): Promise<void> => {
    const state = store.getState();
    if (state.gameId === gameId && (state.status === 'ready' || state.status === 'loading' || state.status === 'scanning')) return;
    store.setState({ gameId, status: 'loading', puzzles: [], attempts: {}, solving: undefined, error: undefined });
    try {
      const { list, attempts } = await readStored(gameId);
      if (store.getState().gameId !== gameId) return;
      if (list) {
        store.setState({ status: 'ready', puzzles: list.puzzles, attempts: attempts.attempts });
        deps.onReady?.(list.puzzles);
        return;
      }
      const existing = await analysis.get(gameId);
      if (!existing?.complete) {
        const status = analysis.status(gameId);
        store.setState({ status: 'scanning', scanned: status?.done ?? existing?.positions.length ?? 0, total: status?.total ?? existing?.total ?? 0 });
      }
      const scan = await analysis.scan(gameId);
      if (store.getState().gameId !== gameId) return;
      const made = await generate(gameId, scan);
      if (store.getState().gameId !== gameId) return;
      store.setState({ status: 'ready', puzzles: made.puzzles, attempts: attempts.attempts });
      deps.onReady?.(made.puzzles);
    } catch (error) {
      if (store.getState().gameId !== gameId) return;
      const message = error instanceof Error ? error.message : String(error);
      log?.warn('puzzles could not be made', { gameId, error: message });
      store.setState({ status: 'failed', error: message });
    }
  };

  return { folderOf, readStored, markOpening, generate, load };
}

export type ListLoader = ReturnType<typeof createListLoader>;
